import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ChevronDown, ChevronRight, Trash2, Sparkles, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'

interface SortableSectionProps {
  id: string
  index: number
  title: string
  typeLabel: string
  isExpanded: boolean
  onToggleExpand: () => void
  onRemove: () => void
  onTitleChange: (title: string) => void
  onRegenerateWithAI?: () => void
  isRegenerating?: boolean
  children: React.ReactNode
}

export function SortableSection({
  id,
  title,
  typeLabel,
  isExpanded,
  onToggleExpand,
  onRemove,
  onTitleChange,
  onRegenerateWithAI,
  isRegenerating,
  children,
}: SortableSectionProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [localTitle, setLocalTitle] = useState(title)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleTitleSubmit = () => {
    onTitleChange(localTitle)
    setIsEditingTitle(false)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border border-border rounded-lg bg-background transition-all',
        isDragging && 'opacity-50 shadow-lg scale-[1.02]',
        isExpanded && 'ring-2 ring-primary/20'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border bg-muted/30">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={onToggleExpand}
          className="p-1 hover:bg-muted rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          {isEditingTitle ? (
            <input
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleTitleSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSubmit()
                if (e.key === 'Escape') {
                  setLocalTitle(title)
                  setIsEditingTitle(false)
                }
              }}
              className="input input-sm w-full"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={() => setIsEditingTitle(true)}
              className="text-left w-full"
            >
              <p className="font-medium truncate">{title}</p>
              <p className="text-xs text-muted-foreground">{typeLabel}</p>
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          {onRegenerateWithAI && (
            <button
              type="button"
              onClick={onRegenerateWithAI}
              disabled={isRegenerating}
              className="p-1.5 rounded hover:bg-primary/10 text-primary transition-colors disabled:opacity-50"
              title="Regenerate with AI"
            >
              {isRegenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
            title="Remove section"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content (expandable) */}
      {isExpanded && (
        <div className="p-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  )
}
